// MobileAddMode — Vollbild-Hinzufügen-Modus der mobilen Editor-Ansicht: Kopfzeile mit
// Ablage-Ziel, Umschalter Suche | Vorschläge und der gewählte Reiter (`MobileSearchTab` bzw.
// `MobileSuggestionsTab`); der Filterzustand beider Reiter liegt im Rahmen.
// Rendert im normalen Dokumentfluss ANSTELLE des Deck-Zweigs (nicht als Overlay) — zwei
// gedockte Filterleisten gleichzeitig gibt es so nie.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import Tabs from '../../../../ui/Tabs';
import { useCardBrowserFilters } from '../../hooks/useCardBrowserFilters';
import { commanderColorFilter } from '../../utils/commanderColorFilter';
import { readAddTab, writeAddTab } from '../utils/addTabMemory';
import MobileSearchTab from './MobileSearchTab';
import MobileSuggestionsTab from './MobileSuggestionsTab';

/**
 * @param {number|null} numericDeckId — numerische DB-ID des Decks, für die Vorschläge
 * @param {boolean} canEditDeck — nur dann gibt es den Vorschläge-Reiter samt Umschalter
 * @param {object[]} deckCards — Karten des Decks, für den „✓ im Deck"-Abgleich per oracleId
 * @param {string} targetLabel — Anzeigename des aktuellen Ablage-Ziels
 * @param {() => void} onOpenTargetPicker — öffnet das Kategorie-Sheet des Aufrufers
 * @param {(entry, options) => Promise<{entryId?: number, duplicate: boolean}|null>} addEntry
 *   — gemeinsamer Add-Pfad der Page (Toasts, Undo-Registrierung, Ziel-Handhabung)
 * @param {string|number} targetCategoryId — aktueller Wähler-Wert (`'auto'` oder ID);
 *   die Kandidaten-Schleife friert ihn beim Start ein
 * @param {() => void} onResetTarget — setzt den Wähler auf „Automatisch" zurück
 * @param {(cardId) => void} zeigeKarteImDeck — springt zur genannten Karte im Deck-Zweig;
 *   Ziel des antippbaren Sammel-Toasts (erste erfolgreich hinzugefügte Karte des Batches)
 */
export default function MobileAddMode({
  deckId,
  numericDeckId = null,
  deckCards = [],
  canEditDeck = false,
  targetLabel = 'Automatisch',
  onOpenTargetPicker,
  onDone,
  addEntry,
  targetCategoryId = 'auto',
  onResetTarget,
  undo,
  zeigeKarteImDeck,
}) {
  // Der Hinzufügen-Modus ist ein Vollbild-Werkzeug: die App-Navbar rutscht sonst beim
  // Scrollen wieder herein, weil dieser Zweig den Deck-Inhalt im Dokumentfluss ersetzt und
  // die Scroll-Position dabei in die Auto-Einblend-Zone springt.
  useEffect(() => {
    document.body.classList.add('mobile-fullscreen-tool');
    return () => document.body.classList.remove('mobile-fullscreen-tool');
  }, []);

  // `MobileAddMode` rendert nur mit geladenem Deck: die Commander-Farben stehen schon im
  // ersten Render. Der Filterzustand liegt hier, nicht im Reiter: `Tabs.Panel` baut den
  // inaktiven Reiter ab, und sein State wäre nach jedem Wechsel weg.
  const initialColors = useMemo(() => commanderColorFilter(deckCards), [deckCards]);
  const [searchFilters, patchSearchFilters] = useCardBrowserFilters(initialColors, deckId);
  const [suggestionFilters, patchSuggestionFilters] = useCardBrowserFilters(initialColors, deckId);

  // Ein Reiterwechsel ist kein History-Eintrag: Zurück schließt den Modus als Ganzes.
  const [gemerkterTab, setGemerkterTab] = useState(readAddTab);
  const sichtbarerTab = canEditDeck ? gemerkterTab : 'search';
  const wechsleTab = useCallback((value) => {
    setGemerkterTab(value);
    writeAddTab(value);
  }, []);

  const searchTab = (
    <MobileSearchTab
      deckId={deckId}
      deckCards={deckCards}
      canEditDeck={canEditDeck}
      filters={searchFilters}
      onFiltersChange={patchSearchFilters}
      addEntry={addEntry}
      targetCategoryId={targetCategoryId}
      onResetTarget={onResetTarget}
      undo={undo}
      zeigeKarteImDeck={zeigeKarteImDeck}
    />
  );

  const header = (
    <div className="mobile-add-header">
      <span className="mobile-add-header__title">Hinzufügen</span>
      <button
        type="button"
        className="btn-secondary mobile-add-header__target"
        onClick={onOpenTargetPicker}
      >
        nach: {targetLabel}
        <ChevronDown size={14} aria-hidden />
      </button>
      <button type="button" className="btn-secondary mobile-add-header__done" onClick={onDone}>
        <X size={16} aria-hidden /> Fertig
      </button>
    </div>
  );

  return (
    <div className="mobile-add-mode">
      {canEditDeck ? (
        <Tabs value={sichtbarerTab} onValueChange={wechsleTab} dense>
          <div className="mobile-add-head">
            {header}
            <div className="mobile-add-tabs">
              <Tabs.List>
                <Tabs.Trigger value="search">Suche</Tabs.Trigger>
                <Tabs.Trigger value="suggestions">Vorschläge</Tabs.Trigger>
              </Tabs.List>
            </div>
          </div>
          <Tabs.Panel value="search">{searchTab}</Tabs.Panel>
          <Tabs.Panel value="suggestions">
            <MobileSuggestionsTab
              numericDeckId={numericDeckId}
              deckCards={deckCards}
              canAdd={canEditDeck}
              filters={suggestionFilters}
              onFiltersChange={patchSuggestionFilters}
              addEntry={addEntry}
            />
          </Tabs.Panel>
        </Tabs>
      ) : (
        <>
          {header}
          {searchTab}
        </>
      )}
    </div>
  );
}
